import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { useTable, useFilters, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import { isMobile } from 'react-device-detect';
import {
  StyledTable,
  StyledTableContainer,
  StyledThead,
  StyledTbody,
  StyledTh,
  StyledTd,
  StyledTrTh,
  StyledTrTd,
  StyledSorted,
  StyledRatingCell,
  StyledLifetimeCell,
  StyledChartContainer,
  StyledSelect,
  StyledSelectContainer,
  StyledPagination,
  ArrowRight,
  ArrowLeft,
  StyledPaginationButton,
  StyledPageNumber,
  StyledPageLabel,
  StyledReactTooltip,
  InfoDarkAbsolute,
  StyledNumber,
  StyledNumberWrap,
  StyledCircleText,
  StyledTopSection,
  StyledBottomSection,
  StyledMobileSection,
  StyledTitle,
  StyledCellName,
  StyledCell,
  StyledSortSelect,
  StyledSortContainer,
  SortLabel,
  StyledTableCell,
  StyledInTeamIcon,
  StyledStatusIcons,
  StyledStatusIconWrap,
} from './styled';
import {
  InjuredMark,
  YellowCountMark,
  SuspendedMark,
  CaptainMark,
  HappyMark,
  UnhappyMark,
  LoanInMark,
  LoanOutMark,
  ListedMark,
  ListedLoanMark,
  OfferMark,
  AcademyMark,
} from '../../../../../../components/MatchMarks';
import PlayerRating from '../../../../../../components/PlayerRating';
import { DonutChart } from '../../../../../../components/Charts';
import SearchTableFilter from '../../../../../Transfers/components/SearchTableFilter';
import DefaultColumnFilter, {
  isInRange,
} from '../../../../../../components/TableFilters/DefaultColumnFilter';
import exactValueFilter from '../../../../../../components/TableFilters/exactValueFilter';
import {
  transferShowUnshowFilter,
  transfersListRequest,
} from '../../../../../../redux/actions/Transfers';
import {
  getAcademyList,
  getStrategyCaptain,
} from '../../../../../../redux/selector/Team';
import { getTransfersList } from '../../../../../../redux/selector/Transfers';
import { isPremiumUser } from '../../../../../../redux/selector/User';
import { simplifiedPosition } from '../../../../../../service/data';
import useNarrowPane from '../../../../../../hooks/useNarrowPane';
import SquadListCard from './SquadListCard';
import { StyledCardList, StyledEmpty, StyledCount } from './squadCardStyled';

const ADVANCED_FILTER_COLUMNS = [
  'forSale',
  'forLoan',
  'fav_style',
  'fav_mindset',
  'worst_mindset',
  'fav_opponent',
  'worst_opponent',
  'life_stage',
];

const MOBILE_SORT_VALUES = [
  { value: 0, label: 'Name: A-Z' },
  { value: 1, label: 'Age: Youngest to oldest' },
  { value: 2, label: 'Position' },
  { value: 3, label: 'Rating: High to Low' },
  { value: 4, label: 'Potential: High to Low' },
  { value: 5, label: 'Fitness: High to Low' },
];

export default React.memo(function TableContainer({
  columns,
  data,
  getUserToSell,
}) {
  const showOptions = [
    { value: 16, label: '16' },
    (isMobile ? { value: 27, label: '27' } : { value: 30, label: '30' }),
  ];

  const [clicked, setClicked] = useState(null);
  const [tableSize, setTableSize] = React.useState(showOptions[1]);
  const [sortValue, setSortValueState] = useState(MOBILE_SORT_VALUES[2]);
  const academyList = useSelector(getAcademyList);
  const transfersList = useSelector(getTransfersList);
  const strategyCaptain = useSelector(getStrategyCaptain);
  const captainId = strategyCaptain?.player?._id;
  const isPremium = useSelector(isPremiumUser);
  const dispatch = useDispatch();
  const isNarrowPane = useNarrowPane();

  const offerPlayerIds = useMemo(() => {
    const ids = new Set();
    const open = [
      ...(transfersList?.active || []),
      ...(transfersList?.pending || []),
    ];
    open.forEach((offer) => {
      const id =
        offer?.player?._id ||
        offer?.player_id ||
        (typeof offer?.player === 'string' ? offer.player : null);
      if (id) ids.add(id);
    });
    return ids;
  }, [transfersList?.active, transfersList?.pending]);

  useEffect(() => () => dispatch(transferShowUnshowFilter(false)), [dispatch]);

  useEffect(() => {
    dispatch(transfersListRequest({ silent: true }));
  }, [dispatch]);

  const customRange = useMemo(
    () => (rows, id, filterValue) =>
      rows.filter((row) => {
        const rowValue = row.values[id];
        return rowValue !== undefined ? isInRange(rowValue, filterValue) : false;
      }),
    []
  );
  customRange.autoRemove = (val) => !val;

  const filterTypes = useMemo(
    () => ({
      ageRange: customRange,
      equals: exactValueFilter,
      text: (rows, id, filterValue) =>
        rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        }),
    }),
    [customRange]
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    allColumns,
    state: { pageIndex, filters },
    toggleSortBy,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetPage: false,
      initialState: {
        pageIndex: 0,
        pageSize: 30,
        sortBy: [{ id: 'sortPriority', desc: false }],
        hiddenColumns: ADVANCED_FILTER_COLUMNS,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    setPageSize(isNarrowPane ? 80 : tableSize.value);
  }, [isNarrowPane, tableSize.value, setPageSize]);

  const getItemNumber = (index) => {
    if (pageIndex < 1) {
      return index + 1;
    }
    if (pageIndex >= 1) {
      return pageIndex * tableSize.value + index + 1;
    }
  };

  const renderStatusIcons = (row, compact = false, card = false) => {
    const injured = row.original.form === 'Injured'
      || row.original.formId === -1
      || row.original.form_range === -1;
    const suspended = Number(row.original.suspension) > 0;
    const yellowCards = Number(row.original.yellow_cards) || 0;
    const isCaptain =
      captainId && String(captainId) === String(row.original._id);
    const listed = Boolean(row.original.listed);
    const listedLoan = Boolean(row.original.listed_loan);
    const inAcademy = (Array.isArray(academyList) && academyList.includes(row.original._id))
      || row.original.academy?.status === 1;
    const hasOffer = offerPlayerIds.has(row.original._id);
    const loanedIn =
      !isEmpty(row.original.lenderTeam) && !row.original.loanedOut;
    const loanedOut = Boolean(row.original.loanedOut);
    const unhappy = row.original.transfer_feeling === 0;
    const happy = row.original.transfer_feeling === 1;
    if (!injured && !suspended && !yellowCards && !isCaptain && !loanedIn && !loanedOut && !listed && !listedLoan && !hasOffer && !inAcademy && !unhappy && !happy) {
      return compact ? null : <StyledStatusIcons />;
    }
    return (
      <StyledStatusIcons $compact={compact} $card={card}>
        {unhappy && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-unhappy`}>
            <UnhappyMark />
            <StyledReactTooltip
              id={`${row.id}-unhappy`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Unhappy at the club
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {happy && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-happy`}>
            <HappyMark />
            <StyledReactTooltip
              id={`${row.id}-happy`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Happy at the club
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {injured && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-injured`}>
            <InjuredMark />
            <StyledReactTooltip
              id={`${row.id}-injured`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Injured
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {suspended && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-suspended`}>
            <SuspendedMark size={16} />
            <StyledReactTooltip
              id={`${row.id}-suspended`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Suspended: misses the next league or cup match.
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {yellowCards > 0 && !suspended && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-yellow`}>
            <YellowCountMark count={yellowCards} size={16} />
            <StyledReactTooltip
              id={`${row.id}-yellow`}
              type="light"
              effect="solid"
              place="bottom"
            >
              {yellowCards} yellow card{yellowCards > 1 ? 's' : ''} this season
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {isCaptain && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-captain`}>
            <CaptainMark auto={!!strategyCaptain?.auto} />
            <StyledReactTooltip
              id={`${row.id}-captain`}
              type="light"
              effect="solid"
              place="bottom"
            >
              {strategyCaptain?.auto ? 'Captain (auto)' : 'Captain'}
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {loanedIn && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-loaned-in`}>
            <LoanInMark />
            <StyledReactTooltip
              id={`${row.id}-loaned-in`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Loaned in
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {loanedOut && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-loaned-out`}>
            <LoanOutMark />
            <StyledReactTooltip
              id={`${row.id}-loaned-out`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Loaned out
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {listed && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-listed`}>
            <ListedMark />
            <StyledReactTooltip
              id={`${row.id}-listed`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Listed for sale
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {listedLoan && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-listed-loan`}>
            <ListedLoanMark />
            <StyledReactTooltip
              id={`${row.id}-listed-loan`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Listed for loan
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {hasOffer && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-offer`}>
            <OfferMark />
            <StyledReactTooltip
              id={`${row.id}-offer`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Transfer offer
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
        {inAcademy && (
          <StyledStatusIconWrap data-tip data-for={`${row.id}-academy`}>
            <AcademyMark />
            <StyledReactTooltip
              id={`${row.id}-academy`}
              type="light"
              effect="solid"
              place="bottom"
            >
              Academy
            </StyledReactTooltip>
          </StyledStatusIconWrap>
        )}
      </StyledStatusIcons>
    );
  };

  const renderCell = (cell, index) => {
    const loanedIn =
      !isEmpty(cell.row.original.lenderTeam) && !cell.row.original.loanedOut;
    const reverseRating = [...cell.row.original.media_rating];
    reverseRating.reverse();
    switch (cell.column.id) {
      case 'sortPriority':
        return isPremium
          ? `${simplifiedPosition[cell.row.original.fav_position]}/${simplifiedPosition[cell.row.original.second_fav_position]
          }`
          : simplifiedPosition[cell.row.original.fav_position];

      case 'name':
        return (
          <>
            {isMobile ? (
              <>
                <StyledTopSection feeling={cell.row.original.transfer_feeling}>
                  <StyledTitle>
                    {cell.row.original.status ? <StyledInTeamIcon /> : <></>}
                    {cell.row.original.name}
                    {renderStatusIcons(cell.row, true)}
                  </StyledTitle>
                  <StyledChartContainer className="chart-section" small>
                    {reverseRating.map((item, itemIndex) => (
                      <DonutChart
                        key={`${item}${itemIndex}`}
                        index={itemIndex + 1}
                        arcSize={item}
                        small
                      />
                    ))}
                  </StyledChartContainer>
                </StyledTopSection>
                <StyledBottomSection>
                  <StyledMobileSection>
                    <StyledCellName>Position</StyledCellName>
                    <StyledCell>
                      {isPremium
                        ? `${simplifiedPosition[cell.row.original.fav_position]
                        }/${simplifiedPosition[
                        cell.row.original.second_fav_position
                        ]
                        }`
                        : simplifiedPosition[cell.row.original.fav_position]}
                    </StyledCell>
                  </StyledMobileSection>
                  <StyledMobileSection>
                    <StyledCellName>Age</StyledCellName>
                    <StyledCell>{cell.row.original.age}</StyledCell>
                  </StyledMobileSection>
                  <StyledMobileSection className="rating-cell-mobile">
                    <StyledCellName>Rating</StyledCellName>
                    <StyledCell>
                      <StyledRatingCell>
                        {cell.row.original.ratingValue}
                        <PlayerRating rate={cell.row.original.rating_diff} />
                      </StyledRatingCell>
                    </StyledCell>
                  </StyledMobileSection>
                  <StyledMobileSection className="potential-cell-mobile">
                    <StyledCellName>Potential</StyledCellName>
                    <StyledCell>{cell.row.original.potentialValue}</StyledCell>
                  </StyledMobileSection>
                  <StyledMobileSection className="fitness-cell-mobile">
                    <StyledCellName>Fitness</StyledCellName>
                    <StyledCell>{cell.row.original.form}</StyledCell>
                  </StyledMobileSection>
                  <StyledMobileSection>
                    <StyledCellName>Contract</StyledCellName>
                    <StyledCell>{`${cell.row.original.contract_remaining_length > 1 ? `${cell.row.original.contract_remaining_length} seasons` : `${cell.row.original.contract_remaining_length} season`}`}</StyledCell>
                  </StyledMobileSection>
                </StyledBottomSection>
              </>
            ) : (
              <StyledTableCell>
                {cell.row.original.status ? <StyledInTeamIcon /> : <></>}
                {cell.row.original.name}
              </StyledTableCell>
            )}
          </>
        );
      case 'statusIcons':
        return isMobile ? null : renderStatusIcons(cell.row);
      case 'ratingId':
        return (
          <StyledRatingCell isLoaned={cell.row.original.loanedOut || loanedIn}>
            {cell.row.original.ratingValue}
            <PlayerRating
              rate={cell.row.original.rating_diff}
              isLoaned={cell.row.original.loanedOut || loanedIn}
            />
          </StyledRatingCell>
        );
      case 'lifetimeMatches':
        return (
          <StyledLifetimeCell>
            {cell.row.original.lifetimeMatches}
          </StyledLifetimeCell>
        );

      case 'potentialId':
        return cell.row.original.potentialValue;

      case 'formId':
        return cell.row.original.form;

      case 'number':
        return (
          <StyledNumberWrap
            data-tip
            clickable="true"
            data-for={`${index}-number-tooltip}`}
          >
            <StyledNumber feeling={cell.row.original.transfer_feeling}>
              <svg viewBox="0 0 25 25" aria-hidden>
                <StyledCircleText x="12.5" y="12.5">
                  {getItemNumber(index)}
                </StyledCircleText>
              </svg>
            </StyledNumber>
            <StyledReactTooltip
              id={`${index}-number-tooltip}`}
              type="light"
              effect="solid"
              place="bottom"
            >
              A green circle indicates your player really loves it at your club
              while a red circle means they are having a miserable time. Grey
              indicates your player is indifferent or their sentiment is not
              known yet.
            </StyledReactTooltip>
          </StyledNumberWrap>
        );

      case 'media_rating':
        return (
          <StyledChartContainer small>
            {reverseRating.map((item, itemIndex) => (
              <DonutChart
                key={`${item}${itemIndex}`}
                index={itemIndex + 1}
                arcSize={item}
                small
              />
            ))}
          </StyledChartContainer>
        );
      case 'contract_remaining_length':
        return (
          <span>{`${cell.row.original.contract_remaining_length > 1 ? `${cell.row.original.contract_remaining_length} seasons` : `${cell.row.original.contract_remaining_length} season`}`}</span>
        )
      default:
        return cell.render('Cell');
    }
  };

  const setRowSize = (e) => {
    setPageSize(e.value);
    setTableSize(e);
  };

  const mobileSortValues = MOBILE_SORT_VALUES;

  const setSortValue = (e) => {
    setSortValueState(e);
    const x = e.value;
    if (x === 0) toggleSortBy('name', false, false);
    if (x === 1) toggleSortBy('age', false, false);
    if (x === 2) toggleSortBy('sortPriority', false, false);
    if (x === 3) toggleSortBy('ratingId', true, false);
    if (x === 4) toggleSortBy('potentialId', true, false);
    if (x === 5) toggleSortBy('formId', true, false);
  };

  const sortControl = (
    <StyledSortContainer $toolbar={false}>
      <SortLabel>Sort</SortLabel>
      <StyledSortSelect
        isSearchable={false}
        onChange={setSortValue}
        options={mobileSortValues}
        defaultValue={MOBILE_SORT_VALUES[2]}
        menuPlacement="auto"
        placeholder="Position"
        classNamePrefix="react-select"
      />
    </StyledSortContainer>
  );

  if (isNarrowPane) {
    return (
      <>
        <SearchTableFilter
          allColumns={allColumns}
          hideCsv
          filters={filters}
          inline
          sortOptions={mobileSortValues}
          sortValue={sortValue}
          onSortChange={setSortValue}
        />
        <StyledCount>
          {page.length} {page.length === 1 ? 'player' : 'players'}
        </StyledCount>
        {page.length === 0 ? (
          <StyledEmpty>No players match these filters.</StyledEmpty>
        ) : (
          <StyledCardList>
            {page.map((row) => {
              prepareRow(row);
              return (
                <SquadListCard
                  key={row.id}
                  player={row.original}
                  statusIcons={renderStatusIcons(row, true, true)}
                  isPremium={isPremium}
                  selected={clicked === row.id}
                  onClick={() => {
                    setClicked(row.id);
                    getUserToSell(row.original);
                  }}
                />
              );
            })}
          </StyledCardList>
        )}
      </>
    );
  }

  return (
    <>
      <SearchTableFilter allColumns={allColumns} hideCsv filters={filters} />
      {sortControl}
      <StyledTableContainer>
        <StyledTable className="rtable--flip squad-table" {...getTableProps()}>
          {!isMobile ? (
            <StyledThead>
              {headerGroups.map((headerGroup) => (
                <StyledTrTh {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    if (column.Header === 'Rating') {
                      return (
                        <StyledTh
                          className="rating-cell"
                          type={column.Header}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({ title: '' })
                          )}
                        >
                          {column.render('Header')}
                          <InfoDarkAbsolute
                            data-tip
                            clickable="true"
                            data-for="rating"
                          />

                          <StyledReactTooltip
                            id="rating"
                            type="light"
                            effect="solid"
                            place="bottom"
                          >
                            RATING indicates how "good" a player is and can
                            range from WEAKEST to LEGEND
                          </StyledReactTooltip>
                          <span>
                            {column.isSorted ? (
                              <StyledSorted
                                type={
                                  column.isSortedDesc
                                    ? 'fromMaxToMin'
                                    : 'fromMinToMax'
                                }
                              />
                            ) : (
                              <StyledSorted />
                            )}
                          </span>
                        </StyledTh>
                      );
                    }
                    if (column.Header === 'Potential') {
                      return (
                        <StyledTh
                          className="potential-cell"
                          type={column.Header}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({ title: '' })
                          )}
                        >
                          {column.render('Header')}
                          <InfoDarkAbsolute
                            data-tip
                            clickable="true"
                            data-for="potential"
                          />

                          <StyledReactTooltip
                            id="potential"
                            type="light"
                            effect="solid"
                            place="bottom"
                          >
                            POTENTIAL shows how “good” a player might become
                            over time. Players develop by playing matches. It’s
                            rare, but possible, for players to develop beyond
                            their potential
                          </StyledReactTooltip>
                          <span>
                            {column.isSorted ? (
                              <StyledSorted
                                type={
                                  column.isSortedDesc
                                    ? 'fromMaxToMin'
                                    : 'fromMinToMax'
                                }
                              />
                            ) : (
                              <StyledSorted />
                            )}
                          </span>
                        </StyledTh>
                      );
                    }
                    if (column.Header === 'Fitness') {
                      return (
                        <StyledTh
                          className="fitness-cell"
                          type={column.Header}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({ title: '' })
                          )}
                        >
                          {column.render('Header')}
                          <InfoDarkAbsolute
                            data-tip
                            clickable="true"
                            data-for="fitness"
                          />

                          <StyledReactTooltip
                            id="fitness"
                            type="light"
                            effect="solid"
                            place="bottom"
                          >
                            FITNESS is an indication of how fit, or tired, a
                            player is. It can range from INJURED to GREAT.
                            Fitness impacts a player’s performance during
                            matches. Players recover fitness when not playing
                            matches
                          </StyledReactTooltip>
                          <span>
                            {column.isSorted ? (
                              <StyledSorted
                                type={
                                  column.isSortedDesc
                                    ? 'fromMaxToMin'
                                    : 'fromMinToMax'
                                }
                              />
                            ) : (
                              <StyledSorted />
                            )}
                          </span>
                        </StyledTh>
                      );
                    }
                    if (column.Header === 'Media Rating') {
                      return (
                        <StyledTh
                          className="media-rating-cell"
                          type={column.Header}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({ title: '' })
                          )}
                        >
                          {column.render('Header')}

                          <InfoDarkAbsolute
                            data-tip
                            clickable="true"
                            data-for="media-rating"
                          />

                          <StyledReactTooltip
                            id="media-rating"
                            type="light"
                            effect="solid"
                            place="bottom"
                          >
                            MEDIA RATING is a measure of how well a player
                            played in recent matches. 10 means the player did
                            exceptionally well. 1 means the player performed
                            poorly
                          </StyledReactTooltip>

                          <span>
                            {column.isSorted ? (
                              <StyledSorted
                                type={
                                  column.isSortedDesc
                                    ? 'fromMaxToMin'
                                    : 'fromMinToMax'
                                }
                              />
                            ) : (
                              <StyledSorted />
                            )}
                          </span>
                        </StyledTh>
                      );
                    }
                    if (column.id === 'statusIcons') {
                      return (
                        <StyledTh
                          type="Status"
                          {...column.getHeaderProps()}
                        />
                      );
                    }
                    return (
                      <StyledTh
                        type={column.Header}
                        {...column.getHeaderProps(
                          column.getSortByToggleProps({ title: '' })
                        )}
                      >
                        {column.render('Header')}
                        <span>
                          {column.isSorted ? (
                            <StyledSorted
                              type={
                                column.isSortedDesc
                                  ? 'fromMaxToMin'
                                  : 'fromMinToMax'
                              }
                            />
                          ) : (
                            <StyledSorted />
                          )}
                        </span>
                      </StyledTh>
                    );
                  })}
                </StyledTrTh>
              ))}
            </StyledThead>
          ) : (
            <></>
          )}
          <StyledTbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              return (
                <StyledTrTd
                  className={`row-number-${row.id}`}
                  clicked={clicked}
                  index={index}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell) => (
                    <StyledTd
                      onClick={() => {
                        setClicked(row.id);
                        getUserToSell(cell.row.original);
                      }}
                      isLoaned={cell.row.original.loanedOut}
                      type={cell.column.Header}
                      data-main={cell.row.original.status === 1}
                      titleCell={cell.column.Header === 'Number'}
                      {...cell.getCellProps()}
                    >
                      {renderCell(cell, index)}
                    </StyledTd>
                  ))}
                </StyledTrTd>
              );
            })}
          </StyledTbody>
        </StyledTable>
      </StyledTableContainer>
      <StyledPagination>
        <StyledPageNumber>
          <StyledPaginationButton
            type="button"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            <ArrowLeft />
          </StyledPaginationButton>
          <StyledPageLabel>
            Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
          </StyledPageLabel>
          <StyledPaginationButton
            type="button"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            <ArrowRight />
          </StyledPaginationButton>
        </StyledPageNumber>

        <StyledSelectContainer>
          <span>Showing players: </span>
          <StyledSelect
            classNamePrefix="react-select"
            defaultValue={showOptions[1]}
            value={tableSize}
            options={showOptions}
            onChange={setRowSize}
            menuPlacement="auto"
          />
        </StyledSelectContainer>
      </StyledPagination>
    </>
  );
});
